import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { CloseCircleOutlined } from '@ant-design/icons';
import { Badge, Checkbox, Flex } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import DeviceContext from '@totem/components/devices/devicesContainer/deviceContainerContext';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import VendorDisplay from '@totem/components/vendors/vendorLogos/vendorDisplay';
import { DeviceQueryResult } from '@totem/types/devices';
import { isNotNull, isNull } from '@totem/utilities/common';

import '../../../../devices/devices.css';

type Props = {
  showMultiselect?: boolean;
  keyType: string;
};

const NinjaOneDeviceTable = ({ showMultiselect, keyType }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const {
    loading,
    deviceData,
    totalRecords,
    selectedDevices,
    selectDevice,
    deselectDevice,
    clearSelectedDevices,
  } = useContext(DeviceContext);
  const { input, handleTableChange, getFilterValues, getFilterOptions } =
    useContext(InputContext);

  const canMultiSelect =
    permissions.organizationalAdministrator.hasPermissions(userProfile) &&
    (isNotNull(showMultiselect) ? showMultiselect : false);

  const columns: ColumnProps<DeviceQueryResult>[] = [
    {
      title: 'Name',
      dataIndex: 'device.displayName',
      key: 'displayName',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => (
        <Link to={`/dashboard/devices/${device.device.id}`}>
          {device.device.displayName}
        </Link>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.device.displayName === compB.device.displayName
          ? 0
          : compA.device.displayName > compB.device.displayName
            ? 1
            : -1,
    },
    {
      title: 'Type',
      dataIndex: 'device.type',
      key: 'type',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => device.device.type,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.device.type === compB.device.type
          ? 0
          : compA.device.type > compB.device.type
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('type'),
      filters: getFilterOptions('type'),
    },
    {
      title: 'Vendor',
      dataIndex: 'device.vendor',
      key: 'vendor',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => (
        <VendorDisplay vendorName={device.device.vendor} />
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.device.vendor === compB.device.vendor
          ? 0
          : compA.device.vendor > compB.device.vendor
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('vendor'),
      filters: getFilterOptions('vendor'),
    },
    {
      title: 'OS',
      dataIndex: 'device.operatingSystem',
      key: 'operatingSystem',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => device.device.operatingSystem,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.device.operatingSystem === compB.device.operatingSystem
          ? 0
          : compA.device.operatingSystem > compB.device.operatingSystem
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('operatingSystem'),
      filters: getFilterOptions('operatingSystem'),
    },
    {
      title: 'IP Addresses',
      dataIndex: 'device.ipAddresses',
      key: 'ipAddresses',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => (
        <span>
          {device.device.ipAddresses !== null &&
            device.device.ipAddresses.map((addr, idx) =>
              idx === 0 ? (
                <span key={addr}>{addr}</span>
              ) : (
                <span key={addr}>
                  <br />
                  {addr}
                </span>
              ),
            )}
        </span>
      ),
    },
    {
      title: 'Keys (Type / Key / Last Seen)',
      dataIndex: 'device.keys',
      key: 'keys',
      showSorterTooltip: false,
      render: (_, device: DeviceQueryResult) => (
        <Flex justify={'flex-start'} vertical>
          {device.device.keys !== null &&
            device.device.keys
              .filter((item) => item.type === keyType)
              .map((key, idx) => (
                <Flex justify={'space-between'} key={idx}>
                  <div>{key.type}</div>
                  <div>{key.value}</div>
                  <div>{key.lastSync}</div>
                </Flex>
              ))}
        </Flex>
      ),
    },
  ];

  const getColumns = () => {
    const cols = [...columns];

    if (canMultiSelect) {
      cols.splice(0, 0, {
        title: (
          <IBTooltip content={permissions.organizationalAdministrator.tooltip}>
            <Badge
              title={'Selected Count'}
              count={isNotNull(selectedDevices) ? selectedDevices.length : 0}
              offset={[0, 2]}
            >
              <CloseCircleOutlined
                style={{ marginTop: 8, marginRight: 8 }}
                onClick={() => clearSelectedDevices()}
              />
            </Badge>
          </IBTooltip>
        ),
        render: (_, record: DeviceQueryResult) => (
          <Checkbox
            checked={
              isNull(selectedDevices)
                ? false
                : selectedDevices.findIndex(
                    (chk) => chk.device.id === record.device.id,
                  ) >= 0
            }
            onChange={(evt) => {
              if (evt.target.checked) {
                selectDevice(record);
              } else {
                deselectDevice(record);
              }
            }}
          />
        ),
      });
    }

    return cols;
  };

  const getDevices = () => {
    if (deviceData !== null) {
      if (deviceData.devices !== null) {
        return deviceData.devices;
      }
    }
    return [];
  };

  return (
    <IBTable
      showSorterTooltip
      columns={getColumns()}
      dataSource={getDevices()}
      loading={loading}
      onChange={(updatedPagination, filters, sorter, extra) =>
        handleTableChange(updatedPagination, filters, sorter, extra)
      }
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.device.id}
    />
  );
};

export default NinjaOneDeviceTable;
